/**
 * DPDP 2023 consent + erasure surface.
 */
import { DATA_CATEGORIES, getConsentMap, grant, revoke, requestErasure, exportAuditLog, getPolicyVersion } from '../chassis/dpdp.ts';

const TPL = /* html */ `
<style>
:host {
  display: block;
  margin: 24px 0 20px;
}
details {
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  background: oklch(98% 0.010 80);
  padding: 14px 18px;
  border-radius: 2px;
}
summary {
  font-family: var(--mono, monospace);
  font-size: 10px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
}
.body {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed oklch(60% 0.040 50 / 0.4);
}
.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-family: var(--mono, monospace);
  font-size: 11px;
}
.row span { letter-spacing: 0.1em; }
.row button {
  appearance: none;
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 5px 10px;
  background: oklch(99% 0.005 80);
  border: 1px solid var(--rule-strong, oklch(28% 0.04 50));
  cursor: pointer;
  border-radius: 2px;
}
.row button.granted { background: var(--crimson, #9b1c1c); color: var(--paper, #f5ead1); border-color: var(--crimson, #9b1c1c); }
.erasure {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed oklch(60% 0.040 50 / 0.4);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.erasure button {
  appearance: none;
  font-family: var(--mono, monospace);
  font-size: 9px;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  padding: 8px 14px;
  background: oklch(99% 0.005 80);
  border: 1px solid var(--crimson, #9b1c1c);
  color: var(--crimson, #9b1c1c);
  cursor: pointer;
  border-radius: 2px;
}
.policy {
  font-family: var(--mono, monospace);
  font-size: 8.5px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint, oklch(50% 0.04 50));
  margin-top: 10px;
}
</style>
<details>
  <summary>
    <span>Data · DPDP 2023</span>
    <span>v${getPolicyVersion()}</span>
  </summary>
  <div class="body" id="body"></div>
</details>
`;

export class HisaabConsentBar extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    this.#render();
  }

  #render(): void {
    const body = this.#shadow.getElementById('body') as HTMLDivElement;
    const map = getConsentMap();
    const rows = DATA_CATEGORIES.map((c) => {
      const granted = map[c]?.granted === true;
      const label = c.replace(/_/g, ' ');
      return `<div class="row"><span>${label}</span><button class="${granted ? 'granted' : ''}" data-cat="${c}">${granted ? 'granted' : 'grant'}</button></div>`;
    }).join('');
    body.innerHTML = `
      ${rows}
      <div class="erasure">
        <span style="font-family:var(--mono,monospace);font-size:10px;letter-spacing:0.18em;">Right to erasure</span>
        <button id="erase">Erase all on-device</button>
      </div>
      <div class="policy">Policy version ${getPolicyVersion()}. Audit log on-device.</div>
    `;
    body.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((b) => {
      b.addEventListener('click', () => {
        const cat = b.dataset.cat;
        if (typeof cat !== 'string') return;
        const cur = getConsentMap()[cat as keyof ReturnType<typeof getConsentMap>];
        if (cur?.granted === true) revoke(cat as Parameters<typeof revoke>[0]);
        else grant(cat as Parameters<typeof grant>[0]);
        this.#render();
      });
    });
    const erase = body.querySelector('#erase') as HTMLButtonElement | null;
    if (erase !== null) {
      erase.addEventListener('click', async () => {
        if (!confirm('Erase all on-device data? Audit log will retain the erasure event.')) return;
        await requestErasure();
        const log = exportAuditLog();
        alert(`Erased. Audit log has ${log.length} entries (in-memory only now).`);
        location.reload();
      });
    }
  }
}

customElements.define('hisaab-consent-bar', HisaabConsentBar);
